import { asString, asTrimmedString } from '@the-hive-frontend/event-domain';

import {
  AttendeeContext,
  EventScope,
  HiveRole,
  SessionUser,
} from './session.types';

const ATTENDEE_ID_CLAIM = 'attendee_id';
const EVENT_SCOPE_CLAIM = 'event_scope';
const ALL_SCOPE = 'all';

const KNOWN_ROLES: ReadonlySet<string> = new Set<string>([
  'super-admin',
  'agency-editor',
  'client-editor',
  'client-viewer',
  'scanner-operator',
  'attendee',
]);

function normaliseRole(value: string): string {
  return value
    .trim()
    .replace(/^\/+/, '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
}

function firstKnownRole(candidates: ReadonlyArray<string>): HiveRole | null {
  for (const candidate of candidates) {
    const normalised = normaliseRole(candidate);
    if (KNOWN_ROLES.has(normalised)) {
      return normalised as HiveRole;
    }
  }
  return null;
}

function onlyStrings(values: ReadonlyArray<unknown>): ReadonlyArray<string> {
  return values.filter((value): value is string => typeof value === 'string');
}

function collectCompanyRoles(
  claims: Record<string, unknown>
): ReadonlyArray<string> {
  const value = claims['company_roles'];
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  const entries = Object.values(value as Record<string, unknown>);
  return onlyStrings(
    entries.flatMap((entry) => (Array.isArray(entry) ? entry : []))
  );
}

function collectGroups(claims: Record<string, unknown>): ReadonlyArray<string> {
  const value = claims['groups'];
  if (!Array.isArray(value)) {
    return [];
  }
  return onlyStrings(value);
}

export function extractRole(
  claims: Record<string, unknown> | null
): HiveRole | null {
  if (!claims) {
    return null;
  }
  const fromCompanyRoles = firstKnownRole(collectCompanyRoles(claims));
  if (fromCompanyRoles) {
    return fromCompanyRoles;
  }
  return firstKnownRole(collectGroups(claims));
}

export function extractOrganisationId(
  claims: Record<string, unknown> | null
): string | null {
  if (!claims) {
    return null;
  }
  return asString(claims['company']) ?? asString(claims['companyUuid']);
}

export function extractEventScope(
  claims: Record<string, unknown> | null
): EventScope {
  if (!claims) {
    return { kind: 'all' };
  }
  const value = claims[EVENT_SCOPE_CLAIM];
  if (value == null || value === ALL_SCOPE) {
    return { kind: 'all' };
  }
  if (!Array.isArray(value)) {
    return { kind: 'all' };
  }

  // An EMPTY array is unrestricted, not "no events". EVT-001: an unrestricted user has no scope
  // rows, so the API and the token both carry []. Reading it as a listed-empty set made
  // isEventInScope false for every event, locking exactly those users out of everything.
  //
  // Tested on the RAW array, before onlyStrings. Filtering first would collapse a genuinely
  // restricted-but-malformed claim — event_scope: [123] — to an empty array and therefore to "all
  // events", turning a parse failure into an access grant. A scope we cannot read must deny, not
  // admit.
  if (value.length === 0) {
    return { kind: 'all' };
  }

  return { kind: 'listed', eventIds: new Set(onlyStrings(value)) };
}

export function mapSessionUser(
  claims: Record<string, unknown> | null
): SessionUser | null {
  if (!claims) {
    return null;
  }
  const email = asString(claims['email']) ?? '';
  return {
    id: asString(claims['sub']) ?? '',
    email,
    firstName: asString(claims['given_name']) ?? '',
    lastName: asString(claims['family_name']) ?? '',
    username: asString(claims['preferred_username']) ?? email,
    role: extractRole(claims),
    organisationId: extractOrganisationId(claims),
    eventScope: extractEventScope(claims),
  };
}

function scopedEventId(value: unknown): string | null {
  const id = asTrimmedString(value);
  return id === ALL_SCOPE ? null : id;
}

function singleEventId(value: unknown): string | null {
  if (typeof value === 'string') {
    return scopedEventId(value);
  }
  if (!Array.isArray(value) || value.length !== 1) {
    return null;
  }
  const only: unknown = value[0];
  return scopedEventId(only);
}

export function extractAttendeeContext(
  claims: Record<string, unknown> | null
): AttendeeContext | null {
  if (!claims) {
    return null;
  }
  const attendeeId = asTrimmedString(claims[ATTENDEE_ID_CLAIM]);
  if (!attendeeId) {
    return null;
  }
  const eventId = singleEventId(claims[EVENT_SCOPE_CLAIM]);
  if (!eventId) {
    return null;
  }
  return { attendeeId, eventId };
}
